import { yupResolver } from "@hookform/resolvers/yup";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import AlertDialog from "../../../../ui/components/alert-dialog/alert-dialog";
import CustomInput from "../../../../ui/components/custom-input/custom-input";
import CustomSelect from "../../../../ui/components/custom-select/custom-select";
import DateInput from "../../../../ui/components/date-input/date-input";
import { ListModel } from "../../../models/list-model";
import { ProfesionalFicha } from "../../../models/profesional-ficha";
import { ProfesionalStatus } from "../../../models/profesional-status";
import { condicionFiscalServices } from "../../../services/condicion-fiscal.services";
import { profesionalServices } from "../../../services/profesional.services";
import { tituloServices } from "../../../services/titulo.services";
import { handle400Error } from "../../../utils/handle-400-errors";
import { localidades } from "../../../utils/localidades";
import {
  AREAS_OPTIONS,
  POBLACION_OPTIONS,
  REDES_CONFIG,
  RedKey,
  buildRedes,
  formatCuit,
  getRedUrl,
} from "../../../utils/profesional-ficha.utils";
import ChipGroup from "../chip-group/chip-group";
import "./profesional-edit.scss";

// Mismo patrón de contraseña que ChangePasswordDialog (autogestión de la
// propia contraseña) para que el reset por parte de un admin exija el mismo
// nivel de seguridad.
const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)\S{8,16}$/;

const schema = yup
  .object()
  .shape({
    nombre: yup
      .string()
      .required("Campo requerido")
      .matches(/^[^0-9]+$/, "No se permiten números"),
    apellido: yup
      .string()
      .required("Campo requerido")
      .matches(/^[^0-9]+$/, "No se permiten números"),
    dni: yup.string().required("Campo requerido"),
    cuit: yup
      .string()
      .required("Campo requerido")
      .matches(/^\d{2}-\d{8}-\d{1}$/, "Formato inválido (99-99999999-9)"),
    tituloId: yup.string().required("Campo requerido"),
    expedidoPor: yup.string().required("Campo requerido"),
    expedidoFecha: yup.date().nullable(),
    matriculaHab: yup.string(),
    matriculaHabFecha: yup.date().nullable(),
    matriculaProf: yup.string().required("Campo requerido"),
    matriculaProfFecha: yup.date().nullable(),
    domicilioReal: yup.string().required("Campo requerido"),
    localidadReal: yup.string().required("Campo requerido"),
    provinciaReal: yup.string(),
    cpReal: yup.string().required("Campo requerido"),
    telefonoReal: yup.string(),
    domicilioLaboral: yup.string(),
    localidadLaboral: yup.string(),
    provinciaLaboral: yup.string(),
    cpLaboral: yup.string(),
    telefonoLaboral: yup.string(),
    condicionFiscalId: yup.string().required("Campo requerido"),
    iibbTipo: yup.string(),
    iibbNumero: yup.string(),
    observacion: yup.string().max(500, "Máximo 500 caracteres"),
  })
  .required();

export default function ProfesionalEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    register,
    getFieldState,
    formState,
    handleSubmit,
    formState: { errors },
    control,
    setError,
    reset,
    getValues,
    setValue,
  } = useForm<any>({
    mode: "all",
    resolver: yupResolver(schema),
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ficha, setFicha] = useState<ProfesionalFicha | null>(null);
  const [active, setActive] = useState(true);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [toggling, setToggling] = useState(false);

  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetPasswordToast, setResetPasswordToast] = useState(false);

  const [titulos, setTitulos] = useState<ListModel[]>([]);
  const [condicionesFiscales, setCondicionesFiscales] = useState<
    ListModel[]
  >([]);
  const localidadOptions: ListModel[] = localidades.map((loc) => ({
    value: loc,
    label: loc,
  }));

  const [iibb, setIibb] = useState(false);
  const [atiendeEnConsultorio, setAtiendeEnConsultorio] = useState(false);
  const [atiendePorObraSocial, setAtiendePorObraSocial] = useState(false);
  const [areas, setAreas] = useState<string[]>([]);
  const [poblacion, setPoblacion] = useState<string[]>([]);
  const [redUrls, setRedUrls] = useState<Record<RedKey, string>>({
    facebook: "",
    instagram: "",
    linkedin: "",
    "x.com": "",
  });
  const setRedUrl = (key: RedKey, val: string) =>
    setRedUrls((prev) => ({ ...prev, [key]: val }));

  useEffect(() => {
    tituloServices
      .lookup()
      .then((response) => {
        setTitulos(
          response.data.map((element: any) => ({
            value: element.id,
            label: element.value,
          }))
        );
      })
      .catch(() => {});
    condicionFiscalServices
      .lookup()
      .then((response) => {
        setCondicionesFiscales(
          response.data.map((element: any) => ({
            value: element.id,
            label: element.value,
          }))
        );
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    profesionalServices
      .getFicha(id)
      .then(({ data }: { data: ProfesionalFicha }) => {
        reset({
          ...data,
          cuit: formatCuit(data.cuit ?? ""),
          expedidoFecha: data.expedidoFecha
            ? new Date(data.expedidoFecha)
            : undefined,
          matriculaHabFecha: data.matriculaHabFecha
            ? new Date(data.matriculaHabFecha)
            : undefined,
          matriculaProfFecha: data.matriculaProfFecha
            ? new Date(data.matriculaProfFecha)
            : undefined,
        });
        setFicha(data);
        setActive(data.active);
        setIibb(data.iibb ?? false);
        setAtiendeEnConsultorio(data.atiendeEnConsultorio ?? false);
        setAtiendePorObraSocial(data.atiendePorObraSocial ?? false);
        setAreas(data.areas ?? []);
        setPoblacion(data.poblacion ?? []);
        setRedUrls({
          facebook: getRedUrl(data.redes ?? [], "facebook"),
          instagram: getRedUrl(data.redes ?? [], "instagram"),
          linkedin: getRedUrl(data.redes ?? [], "linkedin"),
          "x.com": getRedUrl(data.redes ?? [], "x.com"),
        });
      })
      .finally(() => setLoading(false));
  }, [id, reset]);

  const onSubmit = handleSubmit(async (data) => {
    if (!id) return;
    setSaving(true);
    const payload = {
      ...data,
      cuit: data.cuit.replace(/\D/g, ""),
      iibb,
      atiendeEnConsultorio,
      atiendePorObraSocial,
      areas,
      poblacion,
      redes: buildRedes(redUrls),
    };
    profesionalServices
      .updateFicha(id, payload)
      .then(() => {
        navigate("/profesionales", {
          state: {
            textAlert: `El profesional ${data.nombre} ${data.apellido} fue actualizado correctamente.`,
          },
        });
      })
      .catch((error) => {
        if (error?.response?.status === 400) {
          handle400Error(error.response.data.errors, setError);
        }
      })
      .finally(() => setSaving(false));
  });

  const toggleActive = () => {
    if (!id) return;
    setToggling(true);
    const action = active
      ? profesionalServices.desactivar
      : profesionalServices.activar;
    action(id, getValues("observacion"))
      .then(({ data }: { data: ProfesionalStatus }) => {
        setActive(data.active);
        setValue("observacion", data.observacion ?? "");
      })
      .finally(() => {
        setToggling(false);
        setShowStatusDialog(false);
      });
  };

  const closeResetPasswordDialog = () => {
    setShowResetPasswordDialog(false);
    setNewPassword("");
    setConfirmPassword("");
    setShowNewPassword(false);
    setResetPasswordError("");
    setConfirmPasswordError("");
  };

  const handleResetPassword = () => {
    if (!id) return;
    let hasError = false;
    if (!PASSWORD_PATTERN.test(newPassword)) {
      setResetPasswordError(
        "Debe tener entre 8 y 16 caracteres, sin espacios, con al menos una mayúscula, una minúscula y un número."
      );
      hasError = true;
    } else {
      setResetPasswordError("");
    }
    if (confirmPassword !== newPassword) {
      setConfirmPasswordError("Las contraseñas no coinciden.");
      hasError = true;
    } else {
      setConfirmPasswordError("");
    }
    if (hasError) return;

    setResettingPassword(true);
    profesionalServices
      .resetPassword(id, { newPassword, confirmPassword })
      .then(({ data }) => {
        setFicha((prev) => (prev ? { ...prev, userName: data.userName } : prev));
        closeResetPasswordDialog();
        setResetPasswordToast(true);
      })
      .catch((error) => {
        const apiErrors = error?.response?.data?.errors;
        const newPasswordMsg = apiErrors?.newPassword?.[0]?.message;
        const confirmPasswordMsg = apiErrors?.confirmPassword?.[0]?.message;
        if (newPasswordMsg) setResetPasswordError(newPasswordMsg);
        if (confirmPasswordMsg) setConfirmPasswordError(confirmPasswordMsg);
        if (!newPasswordMsg && !confirmPasswordMsg) {
          setResetPasswordError(
            error?.response?.data?.message ||
              "No se pudo restablecer la contraseña. Intente nuevamente."
          );
        }
      })
      .finally(() => setResettingPassword(false));
  };

  const inputProps = { register, getFieldState, formState, errors };

  if (loading) {
    return (
      <div className="profesional-edit-state">
        <CircularProgress size={40} />
        <span>Cargando ficha del profesional...</span>
      </div>
    );
  }

  return (
    <div className="profesional-edit-container">
      <div className="profesional-edit-header">
        <Typography variant="h5" gutterBottom>
          Editar Profesional
        </Typography>
        <div className="profesional-edit-header__status">
          <Chip
            label={active ? "ACTIVO" : "INACTIVO"}
            color={active ? "success" : "default"}
          />
          <Button
            variant="outlined"
            color={active ? "error" : "success"}
            onClick={() => setShowStatusDialog(true)}
          >
            {active ? "Dar de baja" : "Activar"}
          </Button>
        </div>
      </div>

      <Typography variant="subtitle1" className="section-title">
        Usuario y acceso
      </Typography>
      <div className="row usuario-acceso-row">
        <TextField
          label="Nombre de usuario"
          value={ficha?.userName || "Sin usuario asociado"}
          fullWidth
          disabled
        />
        <Button
          variant="outlined"
          disabled={!ficha?.userName}
          onClick={() => setShowResetPasswordDialog(true)}
        >
          Restablecer contraseña
        </Button>
      </div>

      <Box component="form" autoComplete="off" onSubmit={onSubmit}>
        <Typography variant="subtitle1" className="section-title">
          Datos personales
        </Typography>
        <div className="row">
          <CustomInput
            name="nombre"
            label="Nombre"
            type="text"
            required
            maxLength={50}
            value={ficha?.nombre}
            {...inputProps}
          ></CustomInput>
          <CustomInput
            name="apellido"
            label="Apellido"
            type="text"
            required
            maxLength={50}
            value={ficha?.apellido}
            {...inputProps}
          ></CustomInput>
        </div>
        <div className="row">
          <CustomInput
            name="dni"
            label="DNI"
            type="text"
            required
            maxLength={20}
            value={ficha?.dni}
            {...inputProps}
          ></CustomInput>
          <CustomInput
            name="cuit"
            label="CUIT"
            type="text"
            required
            maxLength={13}
            mask={formatCuit}
            placeholder="99-99999999-9"
            value={ficha?.cuit}
            {...inputProps}
          ></CustomInput>
        </div>

        <Typography variant="subtitle1" className="section-title">
          Título y matrícula
        </Typography>
        <div className="row">
          <Controller
            name="tituloId"
            control={control}
            render={({ field }) => (
              <CustomSelect
                field={field}
                name="tituloId"
                label="Título"
                isMultiple={false}
                required
                options={titulos}
                {...inputProps}
              ></CustomSelect>
            )}
          />
          <CustomInput
            name="expedidoPor"
            label="Expedido por"
            type="text"
            required
            maxLength={100}
            value={ficha?.expedidoPor}
            {...inputProps}
          ></CustomInput>
          <Controller
            name="expedidoFecha"
            control={control}
            render={({ field }) => (
              <DateInput
                field={field}
                name="expedidoFecha"
                label="Fecha de expedición"
                value={field.value}
                {...inputProps}
              ></DateInput>
            )}
          />
        </div>
        <div className="row">
          <CustomInput
            name="matriculaHab"
            label="Matrícula habilitante"
            type="text"
            maxLength={50}
            value={ficha?.matriculaHab}
            {...inputProps}
          ></CustomInput>
          <Controller
            name="matriculaHabFecha"
            control={control}
            render={({ field }) => (
              <DateInput
                field={field}
                name="matriculaHabFecha"
                label="Fecha matrícula habilitante"
                value={field.value}
                {...inputProps}
              ></DateInput>
            )}
          />
        </div>
        <div className="row">
          <CustomInput
            name="matriculaProf"
            label="Matrícula profesional"
            type="text"
            required
            maxLength={50}
            value={ficha?.matriculaProf}
            {...inputProps}
          ></CustomInput>
          <Controller
            name="matriculaProfFecha"
            control={control}
            render={({ field }) => (
              <DateInput
                field={field}
                name="matriculaProfFecha"
                label="Fecha matrícula profesional"
                value={field.value}
                {...inputProps}
              ></DateInput>
            )}
          />
        </div>

        <Typography variant="subtitle1" className="section-title">
          Domicilio real
        </Typography>
        <div className="row">
          <CustomInput
            name="domicilioReal"
            label="Domicilio"
            type="text"
            required
            maxLength={200}
            value={ficha?.domicilioReal}
            {...inputProps}
          ></CustomInput>
          <Controller
            name="localidadReal"
            control={control}
            render={({ field }) => (
              <CustomSelect
                field={field}
                name="localidadReal"
                label="Localidad"
                isMultiple={false}
                required
                options={localidadOptions}
                {...inputProps}
              ></CustomSelect>
            )}
          />
          <CustomInput
            name="cpReal"
            label="Código postal"
            type="text"
            required
            maxLength={10}
            value={ficha?.cpReal}
            {...inputProps}
          ></CustomInput>
          <CustomInput
            name="telefonoReal"
            label="Teléfono"
            type="text"
            maxLength={100}
            value={ficha?.telefonoReal}
            {...inputProps}
          ></CustomInput>
        </div>

        <Typography variant="subtitle1" className="section-title">
          Domicilio laboral
        </Typography>
        <div className="row">
          <CustomInput
            name="domicilioLaboral"
            label="Domicilio"
            type="text"
            maxLength={200}
            value={ficha?.domicilioLaboral}
            {...inputProps}
          ></CustomInput>
          <Controller
            name="localidadLaboral"
            control={control}
            render={({ field }) => (
              <CustomSelect
                field={field}
                name="localidadLaboral"
                label="Localidad"
                isMultiple={false}
                options={localidadOptions}
                {...inputProps}
              ></CustomSelect>
            )}
          />
          <CustomInput
            name="cpLaboral"
            label="Código postal"
            type="text"
            maxLength={10}
            value={ficha?.cpLaboral}
            {...inputProps}
          ></CustomInput>
          <CustomInput
            name="telefonoLaboral"
            label="Teléfono"
            type="text"
            maxLength={100}
            value={ficha?.telefonoLaboral}
            {...inputProps}
          ></CustomInput>
        </div>

        <Typography variant="subtitle1" className="section-title">
          Situación fiscal
        </Typography>
        <div className="row">
          <Controller
            name="condicionFiscalId"
            control={control}
            render={({ field }) => (
              <CustomSelect
                field={field}
                name="condicionFiscalId"
                label="Condición fiscal"
                isMultiple={false}
                required
                options={condicionesFiscales}
                {...inputProps}
              ></CustomSelect>
            )}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={iibb}
                onChange={(e) => setIibb(e.target.checked)}
              />
            }
            label="Inscripto en IIBB"
          />
        </div>
        {iibb && (
          <div className="row">
            <CustomInput
              name="iibbTipo"
              label="Tipo IIBB"
              type="text"
              maxLength={50}
              value={ficha?.iibbTipo}
              {...inputProps}
            ></CustomInput>
            <CustomInput
              name="iibbNumero"
              label="Número IIBB"
              type="text"
              maxLength={50}
              value={ficha?.iibbNumero}
              {...inputProps}
            ></CustomInput>
          </div>
        )}

        <Typography variant="subtitle1" className="section-title">
          Atención
        </Typography>
        <div className="row">
          <FormControlLabel
            control={
              <Checkbox
                checked={atiendeEnConsultorio}
                onChange={(e) => setAtiendeEnConsultorio(e.target.checked)}
              />
            }
            label="Atiende en consultorio"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={atiendePorObraSocial}
                onChange={(e) => setAtiendePorObraSocial(e.target.checked)}
              />
            }
            label="Atiende por obra social"
          />
        </div>
        <ChipGroup
          label="Áreas de especialización"
          options={AREAS_OPTIONS}
          selected={areas}
          onChange={setAreas}
        />
        <ChipGroup
          label="Población que atiende"
          options={POBLACION_OPTIONS}
          selected={poblacion}
          onChange={setPoblacion}
        />

        <Typography variant="subtitle1" className="section-title">
          Redes y contacto
        </Typography>
        <div className="row">
          {REDES_CONFIG.map(({ key, label, placeholder }) => (
            <TextField
              key={key}
              label={label}
              type="url"
              placeholder={placeholder}
              value={redUrls[key]}
              onChange={(e) => setRedUrl(key, e.target.value)}
              inputProps={{ maxLength: 300 }}
              fullWidth
            />
          ))}
        </div>

        <Typography variant="subtitle1" className="section-title">
          Observación
        </Typography>
        <div className="row">
          <TextField
            label="Observación"
            multiline
            rows={3}
            fullWidth
            error={!!errors.observacion}
            helperText={errors.observacion?.message?.toString()}
            inputProps={{ maxLength: 500 }}
            {...register("observacion")}
          />
        </div>

        <div className="row actions">
          <Button variant="outlined" onClick={() => navigate("/profesionales")}>
            Cancelar
          </Button>
          <Button
            className="create-button"
            variant="contained"
            type="submit"
            disabled={saving}
          >
            {saving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </div>
      </Box>

      <AlertDialog
        show={showStatusDialog}
        setShow={setShowStatusDialog}
        title={active ? "Dar de baja profesional" : "Activar profesional"}
        text={
          active
            ? "¿Confirma que desea dar de baja la matrícula de este profesional? Podrá reactivarla más adelante."
            : "¿Confirma que desea reactivar la matrícula de este profesional?"
        }
        children={
          <Controller
            name="observacion"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Observación"
                multiline
                rows={3}
                fullWidth
                inputProps={{ maxLength: 500 }}
                sx={{ mt: 2 }}
              />
            )}
          />
        }
        actions={
          <>
            <Button onClick={() => setShowStatusDialog(false)}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color={active ? "error" : "success"}
              disabled={toggling}
              onClick={toggleActive}
            >
              Confirmar
            </Button>
          </>
        }
      ></AlertDialog>

      <AlertDialog
        show={showResetPasswordDialog}
        setShow={(show) => (show ? setShowResetPasswordDialog(true) : closeResetPasswordDialog())}
        title="Restablecer contraseña"
        text={`Ingrese la nueva contraseña para el usuario "${ficha?.userName}". El profesional deberá usarla en su próximo inicio de sesión.`}
        children={
          <>
            <TextField
              autoFocus
              label="Nueva contraseña"
              type={showNewPassword ? "text" : "password"}
              fullWidth
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (resetPasswordError) setResetPasswordError("");
              }}
              error={!!resetPasswordError}
              helperText={
                resetPasswordError ||
                "8 a 16 caracteres, sin espacios, con mayúscula, minúscula y número."
              }
              autoComplete="new-password"
              sx={{ mt: 2 }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword((v) => !v)}
                        edge="end"
                        tabIndex={-1}
                      >
                        {showNewPassword ? (
                          <VisibilityOffOutlinedIcon />
                        ) : (
                          <VisibilityOutlinedIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              label="Confirmar contraseña"
              type={showNewPassword ? "text" : "password"}
              fullWidth
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (confirmPasswordError) setConfirmPasswordError("");
              }}
              error={!!confirmPasswordError}
              helperText={confirmPasswordError}
              autoComplete="new-password"
              sx={{ mt: 2 }}
            />
          </>
        }
        actions={
          <>
            <Button onClick={closeResetPasswordDialog} disabled={resettingPassword}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={resettingPassword || !newPassword || !confirmPassword}
              onClick={handleResetPassword}
            >
              {resettingPassword ? "Restableciendo..." : "Confirmar"}
            </Button>
          </>
        }
      ></AlertDialog>

      <Snackbar
        open={resetPasswordToast}
        autoHideDuration={4000}
        onClose={() => setResetPasswordToast(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setResetPasswordToast(false)}
          sx={{ width: "100%" }}
        >
          Contraseña restablecida correctamente.
        </Alert>
      </Snackbar>
    </div>
  );
}
